/**
 * カテゴリ（階層・Box フォルダ連動）の管理。
 *
 * 最大3階層。素材を置けるのは末端（子を持たないカテゴリ）だけで、
 * 素材を持つカテゴリの下に子は作れない（排他）。本体の担保は DB トリガと Worker。
 * ここでのボタンの出し分けは操作性のためで、サーバが 409 で断ればその理由を見せる。
 *
 * 作成・改名・移動・削除は Worker 経由（Box のフォルダも一緒に動く）。
 * 項目の割り当て（category_fields）は Box に関係しないので PostgREST に直接書く（RLS が守る）。
 */
import { useEffect, useMemo, useState } from 'react'
import { supabase, type AssetField, type Category, type CategoryField } from '../../lib/supabase.ts'
import {
  ApiError,
  createCategory,
  updateCategory,
  deleteCategory,
  reconcileCategories,
  type ReconcileIssue,
} from '../../lib/api.ts'
import {
  CATEGORY_MAX_DEPTH,
  buildCategoryTree,
  flattenTree,
  categoryPathLabel,
  canAddChild,
  eligibleParents,
  hasChildren,
  type CategoryNode,
} from '../../lib/categories.ts'
import { effectiveFields, FIELD_TYPE_LABEL } from '../../lib/fields.ts'

/** Worker / DB のエラーコード → 日本語。未知のコードは生値で出す。 */
export function describeCategoryError(code: string): string {
  if (code.startsWith('box_folder_not_empty:')) {
    return `Box のフォルダに ${code.split(':')[1]} 件のファイルが残っています（素材として未登録）。先に取り込むか、Box 側で移してください`
  }
  if (code.startsWith('box_folder_already_used:')) {
    return `同名の Box フォルダが既にカテゴリ「${code.split(':')[1]}」で使われています。別の名称にしてください`
  }
  if (code.startsWith('slug must')) return '識別子は半角英小文字・数字・ハイフンで2〜63文字にしてください'
  const table: Record<string, string> = {
    slug_already_exists: 'その識別子はすでに使われています',
    parent_not_found: '親カテゴリが見つかりません（画面を読み込み直してください）',
    category_not_found: 'カテゴリが見つかりません（画面を読み込み直してください）',
    category_depth_exceeded: `カテゴリの階層は最大 ${CATEGORY_MAX_DEPTH} 段です`,
    category_parent_has_assets: '素材が入っているカテゴリの下に子カテゴリは作れません。先に素材を別のカテゴリへ移してください',
    category_cycle: 'カテゴリを自分自身や自分の配下へは移動できません',
    category_has_children: '子カテゴリがあるカテゴリは削除できません',
    category_has_assets: '素材があるカテゴリは削除できません',
    category_in_use: '削除中に子カテゴリか素材が追加されました。画面を読み込み直してください',
    category_not_leaf: '子カテゴリを持つカテゴリには素材を置けません',
    box_folder_name_in_use: '同じ階層に同名の Box フォルダがあります。別の名称にしてください',
    box_folder_not_found: 'Box 側にフォルダが見つかりません。「Box との整合を確認」で状態を確かめてください',
    permission_denied: '権限がありません',
  }
  if (table[code]) return `${table[code]}（${code}）`
  if (code.startsWith('box_unavailable')) return `Box に接続できませんでした（${code}）`
  return code
}

const errorCode = (e: unknown): string => (e instanceof ApiError ? e.code : e instanceof Error ? e.message : String(e))

type ReconcileResult = { checkedAt: string; categories: number; boxFolders: number; issues: ReconcileIssue[] }

export function CategorySettings() {
  const [cats, setCats] = useState<Category[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [fields, setFields] = useState<AssetField[]>([])
  const [assignments, setAssignments] = useState<CategoryField[]>([])
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /** 開いている下位フォーム。同時に1つだけ。 */
  const [panel, setPanel] = useState<
    | { kind: 'add'; parentId: string | null }
    | { kind: 'edit'; id: string }
    | { kind: 'assign'; id: string }
    | null
  >(null)
  const [reconcile, setReconcile] = useState<ReconcileResult | null>(null)

  const reload = async () => {
    const [catRes, assetRes, fieldRes, assignRes] = await Promise.all([
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('assets').select('category_id'),
      supabase.from('asset_fields').select('*').order('sort_order'),
      supabase.from('category_fields').select('*'),
    ])
    setCats((catRes.data ?? []) as Category[])
    const next: Record<string, number> = {}
    for (const row of (assetRes.data ?? []) as { category_id: string | null }[]) {
      const key = row.category_id ?? '__none__'
      next[key] = (next[key] ?? 0) + 1
    }
    setCounts(next)
    setFields((fieldRes.data ?? []) as AssetField[])
    setAssignments((assignRes.data ?? []) as CategoryField[])
    setLoaded(true)
  }

  useEffect(() => {
    reload()
  }, [])

  const rows = useMemo(() => flattenTree(buildCategoryTree(cats)), [cats])

  const run = async (label: string, fn: () => Promise<string | void>) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const msg = await fn()
      setNotice(msg ?? `${label}しました`)
      setPanel(null)
      await reload()
    } catch (e) {
      setError(`${label}できませんでした: ${describeCategoryError(errorCode(e))}`)
    } finally {
      setBusy(false)
    }
  }

  const remove = (n: CategoryNode) => {
    if (
      !confirm(
        `カテゴリ「${categoryPathLabel(cats, n.id)}」を削除します。Box 側の空フォルダもゴミ箱へ移動します。よろしいですか？`,
      )
    )
      return
    run('削除', async () => {
      const r = await deleteCategory(n.id)
      return r.boxFolder === 'deleted'
        ? '削除しました（Box のフォルダもゴミ箱へ移動しました）'
        : r.boxFolder === 'not_found'
          ? '削除しました（Box 側にフォルダはありませんでした）'
          : '削除しました。Box のフォルダは残っています（「Box との整合を確認」で確認してください）'
    })
  }

  const runReconcile = async () => {
    setBusy(true)
    setError(null)
    try {
      setReconcile(await reconcileCategories())
    } catch (e) {
      setError(`整合チェックに失敗しました: ${describeCategoryError(errorCode(e))}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="categories">
      <p className="muted small">
        カテゴリは Box のフォルダと1対1で対応し、階層も同じ形で Box に作られます（最大 {CATEGORY_MAX_DEPTH} 段）。
        <strong>素材を置けるのは末端のカテゴリだけ</strong>で、素材が入っているカテゴリの下に子は作れません。
        改名・移動をすると Box のフォルダも一緒に動きます。
      </p>

      <div className="cat-toolbar">
        <button type="button" disabled={busy} onClick={() => setPanel({ kind: 'add', parentId: null })}>
          最上位のカテゴリを追加
        </button>
        <button type="button" disabled={busy} onClick={runReconcile}>
          Box との整合を確認
        </button>
      </div>

      {panel?.kind === 'add' && (
        <CategoryForm
          parentLabel={panel.parentId ? categoryPathLabel(cats, panel.parentId) : null}
          busy={busy}
          onCancel={() => setPanel(null)}
          onSubmit={(name, slug) =>
            run('追加', async () => {
              const siblings = cats.filter((c) => c.parent_id === panel.parentId)
              const r = await createCategory({ name, slug, parentId: panel.parentId, sortOrder: siblings.length })
              return r.reusedExistingFolder
                ? `既存の Box フォルダ（${r.boxFolderId}）を使ってカテゴリを追加しました`
                : `Box フォルダ（${r.boxFolderId}）を作成してカテゴリを追加しました`
            })
          }
        />
      )}

      {panel?.kind === 'edit' && (
        <CategoryEditForm
          category={cats.find((c) => c.id === panel.id)!}
          categories={cats}
          busy={busy}
          onCancel={() => setPanel(null)}
          onSubmit={(params) =>
            run('変更', async () => {
              const r = await updateCategory(panel.id, params)
              return r.moved
                ? '変更しました（Box のフォルダも移動しました）'
                : r.renamed
                  ? '変更しました（Box のフォルダも改名しました）'
                  : '変更しました'
            })
          }
        />
      )}

      {panel?.kind === 'assign' && (
        <CategoryFieldAssignment
          category={cats.find((c) => c.id === panel.id)!}
          categories={cats}
          fields={fields}
          assignments={assignments}
          onClose={() => setPanel(null)}
          onSaved={async () => {
            setNotice('項目の割り当てを保存しました')
            await reload()
          }}
        />
      )}

      {notice && <p className="success small">{notice}</p>}
      {error && <p className="error small">{error}</p>}

      <table className="invites cat-table">
        <thead>
          <tr>
            <th>名称</th>
            <th>識別子</th>
            <th>素材</th>
            <th>項目</th>
            <th>Box フォルダID</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => {
            const count = counts[n.id] ?? 0
            const leaf = !hasChildren(cats, n.id)
            const eff = effectiveFields(n.id, cats, fields, assignments)
            const own = eff.filter((f) => f.assignedTo === n.id).length
            return (
              <tr key={n.id}>
                <td className="small cat-name">
                  <span className="cat-indent" style={{ '--depth': n.depth } as React.CSSProperties} />
                  {n.depth > 1 && <span className="muted">└ </span>}
                  {n.name}
                  {leaf ? (
                    <span className="badge-inline">末端</span>
                  ) : (
                    <span className="badge-inline">{n.children.length} 子</span>
                  )}
                </td>
                <td className="mono small">{n.slug}</td>
                <td className="small">{leaf ? count : <span className="muted">—</span>}</td>
                <td className="small">
                  <button type="button" className="link small" disabled={busy} onClick={() => setPanel({ kind: 'assign', id: n.id })}>
                    {eff.length === 0 ? '割り当てる' : `${eff.length} 項目${own < eff.length ? `（継承 ${eff.length - own}）` : ''}`}
                  </button>
                </td>
                <td className="mono small">{n.box_folder_id}</td>
                <td>
                  <div className="cat-actions">
                    {canAddChild(cats, n.id, count) && (
                      <button type="button" className="small" disabled={busy} onClick={() => setPanel({ kind: 'add', parentId: n.id })}>
                        子を追加
                      </button>
                    )}
                    <button type="button" className="small" disabled={busy} onClick={() => setPanel({ kind: 'edit', id: n.id })}>
                      変更
                    </button>
                    {leaf && count === 0 && (
                      <button type="button" className="small danger" disabled={busy} onClick={() => remove(n)}>
                        削除
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {loaded && rows.length === 0 && <p className="muted small">まだカテゴリがありません。</p>}
      {(counts.__none__ ?? 0) > 0 && (
        <p className="warn small">
          未分類の素材が {counts.__none__} 件あります（枝のフォルダに置かれたファイル等）。カタログの「未分類」から末端のカテゴリへ移してください。
        </p>
      )}

      {reconcile && <ReconcileReport result={reconcile} categories={cats} />}
    </section>
  )
}

function CategoryForm({
  parentLabel,
  busy,
  onCancel,
  onSubmit,
}: {
  parentLabel: string | null
  busy: boolean
  onCancel: () => void
  onSubmit: (name: string, slug: string) => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  return (
    <form
      className="subform invite-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(name.trim(), slug.trim().toLowerCase())
      }}
    >
      <h3>{parentLabel ? `「${parentLabel}」の下にカテゴリを追加` : '最上位のカテゴリを追加'}</h3>
      <div className="row">
        <div>
          <label htmlFor="cat-name">名称（日本語可。Box のフォルダ名になります）</label>
          <input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="新幹線" required autoFocus />
        </div>
        <div>
          <label htmlFor="cat-slug">識別子（半角英小文字・全体で一意）</label>
          <input
            id="cat-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="shinkansen"
            pattern="[a-z0-9][a-z0-9-]{1,62}"
            required
          />
        </div>
      </div>
      <div className="staff-actions">
        <button className="primary" disabled={busy || !name || !slug}>
          {busy ? '作成中…' : 'カテゴリを追加'}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onCancel}>
          やめる
        </button>
      </div>
    </form>
  )
}

function CategoryEditForm({
  category,
  categories,
  busy,
  onCancel,
  onSubmit,
}: {
  category: Category
  categories: Category[]
  busy: boolean
  onCancel: () => void
  onSubmit: (params: { name?: string; parentId?: string | null; sortOrder?: number }) => void
}) {
  const [name, setName] = useState(category.name)
  const [parentId, setParentId] = useState<string>(category.parent_id ?? '')
  const [sortOrder, setSortOrder] = useState<string>(String(category.sort_order))
  const parents = useMemo(() => eligibleParents(categories, category.id), [categories, category.id])
  const currentParentEligible = !category.parent_id || parents.some((p) => p.id === category.parent_id)

  return (
    <form
      className="subform invite-form"
      onSubmit={(e) => {
        e.preventDefault()
        const params: { name?: string; parentId?: string | null; sortOrder?: number } = {}
        if (name.trim() !== category.name) params.name = name.trim()
        const nextParent = parentId === '' ? null : parentId
        if (nextParent !== category.parent_id) params.parentId = nextParent
        const n = Number(sortOrder)
        if (Number.isFinite(n) && n !== category.sort_order) params.sortOrder = n
        onSubmit(params)
      }}
    >
      <h3>「{categoryPathLabel(categories, category.id)}」を変更</h3>
      <div className="row">
        <div>
          <label htmlFor="edit-name">名称（Box のフォルダも改名されます）</label>
          <input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label htmlFor="edit-parent">親カテゴリ（変えると Box のフォルダも移動します）</label>
          <select id="edit-parent" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">（最上位）</option>
            {!currentParentEligible && category.parent_id && (
              <option value={category.parent_id}>{categoryPathLabel(categories, category.parent_id)}（現在）</option>
            )}
            {parents.map((p) => (
              <option key={p.id} value={p.id}>
                {categoryPathLabel(categories, p.id)}
              </option>
            ))}
          </select>
          <p className="muted small">
            移動先は、階層が {CATEGORY_MAX_DEPTH} 段に収まり、素材を持たないカテゴリに限られます。
          </p>
        </div>
        <div>
          <label htmlFor="edit-sort">並び順（小さいほど上）</label>
          <input id="edit-sort" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
        </div>
      </div>
      <div className="staff-actions">
        <button className="primary" disabled={busy || !name.trim()}>
          {busy ? '変更中…' : '変更を保存'}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onCancel}>
          やめる
        </button>
      </div>
    </form>
  )
}

/**
 * カテゴリへの項目の割り当て。
 *
 * 祖先から継承した項目は読み取り専用で見せる（外すなら祖先側で外す）。
 * 同じ項目を自分にも割り当てると、必須の有無と並びを上書きできる（近い段が勝つ）。
 * 保存は PostgREST に直接（Box に関係しない。RLS が管理権限に限っている）。
 */
function CategoryFieldAssignment({
  category,
  categories,
  fields,
  assignments,
  onClose,
  onSaved,
}: {
  category: Category
  categories: Category[]
  fields: AssetField[]
  assignments: CategoryField[]
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const inherited = useMemo(
    () =>
      effectiveFields(category.parent_id, categories, fields, assignments),
    [category.parent_id, categories, fields, assignments],
  )
  const [own, setOwn] = useState<{ field_key: string; required: boolean }[]>(() =>
    assignments
      .filter((a) => a.category_id === category.id)
      .sort((a, b) => a.position - b.position)
      .map((a) => ({ field_key: a.field_key, required: a.required })),
  )
  const [adding, setAdding] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const byKey = new Map(fields.map((f) => [f.key, f]))
  const candidates = fields.filter((f) => !own.some((o) => o.field_key === f.key))

  const move = (i: number, dir: -1 | 1) =>
    setOwn((prev) => {
      const next = [...prev]
      const j = i + dir
      if (j < 0 || j >= next.length) return prev
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return next
    })

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const before = assignments.filter((a) => a.category_id === category.id).map((a) => a.field_key)
      const removed = before.filter((k) => !own.some((o) => o.field_key === k))
      if (removed.length) {
        const { error } = await supabase
          .from('category_fields')
          .delete()
          .eq('category_id', category.id)
          .in('field_key', removed)
        if (error) throw new Error(error.message)
      }
      if (own.length) {
        const { error } = await supabase.from('category_fields').upsert(
          own.map((o, i) => ({ category_id: category.id, field_key: o.field_key, position: i, required: o.required })),
          { onConflict: 'category_id,field_key' },
        )
        if (error) throw new Error(error.message)
      }
      await onSaved()
      onClose()
    } catch (e) {
      setError(`保存できませんでした（${e instanceof Error ? e.message : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="subform">
      <h3>「{categoryPathLabel(categories, category.id)}」の項目</h3>
      <p className="muted small">
        ここで割り当てた項目は、このカテゴリと配下のカテゴリの素材で使えます。
        祖先から継承した項目は外せません（祖先側で外してください）。
      </p>

      {inherited.length > 0 && (
        <>
          <p className="small">継承している項目</p>
          <ul className="assign-list">
            {inherited.map((f) => (
              <li key={f.key} className="attr-inherited">
                <span className="grow">
                  {f.label} <span className="muted small">（{FIELD_TYPE_LABEL[f.type]}・{categoryPathLabel(categories, f.assignedTo)} から{f.required ? '・必須' : ''}）</span>
                </span>
                {!own.some((o) => o.field_key === f.key) && (
                  <button type="button" className="small" disabled={busy} onClick={() => setOwn((p) => [...p, { field_key: f.key, required: f.required }])}>
                    ここで上書き
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="small">このカテゴリの項目</p>
      {own.length === 0 ? (
        <p className="muted small">まだありません。</p>
      ) : (
        <ul className="assign-list">
          {own.map((o, i) => {
            const f = byKey.get(o.field_key)
            return (
              <li key={o.field_key}>
                <span className="grow">
                  {f?.label ?? o.field_key}{' '}
                  <span className="muted small">（{f ? FIELD_TYPE_LABEL[f.type] : '不明'}）</span>
                </span>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={o.required}
                    onChange={(e) => setOwn((p) => p.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
                  />
                  必須
                </label>
                <button type="button" className="small" disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="上へ">
                  ↑
                </button>
                <button type="button" className="small" disabled={busy || i === own.length - 1} onClick={() => move(i, 1)} aria-label="下へ">
                  ↓
                </button>
                <button type="button" className="small danger" disabled={busy} onClick={() => setOwn((p) => p.filter((_, j) => j !== i))}>
                  外す
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="staff-actions">
        <select value={adding} onChange={(e) => setAdding(e.target.value)} style={{ width: 'auto', margin: 0 }}>
          <option value="">項目を選ぶ…</option>
          {candidates.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}（{FIELD_TYPE_LABEL[f.type]}）
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || !adding}
          onClick={() => {
            setOwn((p) => [...p, { field_key: adding, required: false }])
            setAdding('')
          }}
        >
          追加
        </button>
      </div>
      {fields.length === 0 && (
        <p className="muted small">項目の台帳が空です。「項目」タブで項目を作ってから割り当ててください。</p>
      )}

      <div className="staff-actions">
        <button type="button" className="primary" disabled={busy} onClick={save}>
          {busy ? '保存中…' : '割り当てを保存'}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onClose}>
          閉じる
        </button>
      </div>
      {error && <p className="error small">{error}</p>}
    </div>
  )
}

const ISSUE_LABEL: Record<ReconcileIssue['kind'], string> = {
  folder_missing_in_box: 'Box にフォルダが無い',
  name_mismatch: 'フォルダ名が DB と違う',
  parent_mismatch: 'フォルダの親が DB と違う',
  files_in_branch: '枝のフォルダに直接ファイルがある',
  unregistered_folder: 'DB に無い Box フォルダ',
  files_in_root: 'ルート直下にファイルがある',
  asset_uncategorized: '未分類の素材',
  asset_folder_mismatch: '素材の Box フォルダがカテゴリと違う',
  unregistered_files: '素材として未登録のファイル',
}

function describeIssue(issue: ReconcileIssue, categories: Category[]): string {
  switch (issue.kind) {
    case 'folder_missing_in_box':
      return `「${categoryPathLabel(categories, issue.categoryId)}」のフォルダ ${issue.boxFolderId} が Box のルート配下に見つかりません`
    case 'name_mismatch':
      return `「${categoryPathLabel(categories, issue.categoryId)}」の Box フォルダ名は「${issue.boxName}」です`
    case 'parent_mismatch':
      return `「${categoryPathLabel(categories, issue.categoryId)}」の Box フォルダは親 ${issue.boxParentFolderId} の下にあります（DB では ${issue.expectedParentFolderId}）`
    case 'files_in_branch':
      return `「${categoryPathLabel(categories, issue.categoryId)}」に ${issue.count} 件: ${issue.files.map((f) => f.name).join('、')}`
    case 'unregistered_folder':
      return `「${issue.name}」（${issue.boxFolderId}、親 ${issue.boxParentFolderId}、ファイル ${issue.fileCount} 件）`
    case 'files_in_root':
      return `${issue.count} 件: ${issue.files.map((f) => f.name).join('、')}`
    case 'asset_uncategorized':
      return `「${issue.title}」（Box フォルダ ${issue.boxFolderId ?? '不明'}）`
    case 'asset_folder_mismatch':
      return `「${issue.title}」は ${issue.actualFolderId} にあります（カテゴリ「${categoryPathLabel(categories, issue.categoryId)}」は ${issue.expectedFolderId}）`
    case 'unregistered_files':
      return `「${categoryPathLabel(categories, issue.categoryId)}」に ${issue.count} 件: ${issue.files.map((f) => f.name).join('、')}（アップロード画面の「Box から取り込む」で登録できます）`
  }
}

function ReconcileReport({ result, categories }: { result: ReconcileResult; categories: Category[] }) {
  const grouped = useMemo(() => {
    const m = new Map<ReconcileIssue['kind'], ReconcileIssue[]>()
    for (const i of result.issues) m.set(i.kind, [...(m.get(i.kind) ?? []), i])
    return [...m.entries()]
  }, [result])

  return (
    <div className="reconcile">
      <h3 className="small">
        Box との整合（{new Date(result.checkedAt).toLocaleString('ja-JP')}・カテゴリ {result.categories} 件／Box フォルダ {result.boxFolders} 件）
      </h3>
      {result.issues.length === 0 ? (
        <p className="success small">ずれはありません。</p>
      ) : (
        grouped.map(([kind, issues]) => (
          <div key={kind}>
            <p className="small">
              <strong>{ISSUE_LABEL[kind]}</strong> <span className="muted">{issues.length} 件</span>
            </p>
            <ul className="small">
              {issues.map((i, idx) => (
                <li key={idx}>{describeIssue(i, categories)}</li>
              ))}
            </ul>
          </div>
        ))
      )}
      <p className="muted small">
        直すのは手作業です。Box 側で動かしたフォルダは DB の親・名称に合わせ、DB 側の変更は「変更」から行ってください。
      </p>
    </div>
  )
}
